import { CalendarClock, Crown, Mail, Package } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { GroupBlock } from "@/components/subscriptions/GroupBlock";
import { BusinessPlans } from "@/components/subscriptions/BusinessPlans";
import { ProfitDot } from "@/components/admin/Profit";

export const STATUSES = ["active", "cancelling", "late", "vip", "suspended", "never_paid"];
export const PLANS = ["standard", "pro", "ultra", "custom"];
const TONE = {
  active: "border-emerald-500/60 text-emerald-200",
  cancelling: "border-amber-400/60 text-amber-200",
  late: "border-orange-500/60 text-orange-200",
  vip: "border-[var(--gold)] text-[var(--gold-bright)]",
  suspended: "border-red-500/60 text-red-200",
  never_paid: "border-neutral-600 text-neutral-300",
};

export const useMoney = () => {
  const { locale } = useI18n();
  return (v, cur) => (v == null ? "—" : new Intl.NumberFormat(locale, { style: "currency", currency: cur.toUpperCase(), maximumFractionDigits: 2 }).format(v));
};

export const planLabel = (t, plan, name) => (plan === "custom" ? t("subs.custom") : name);

export const StatusPill = ({ status, testId }) => {
  const { t } = useI18n();
  return (
    <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide ${TONE[status]}`} data-testid={testId}>
      <span className="min-w-0 break-words">{t(`subs.st.${status}`)}</span>
    </span>
  );
};

const AppsBar = ({ used, max }) => {
  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800" aria-hidden>
      <div className={`h-full rounded-full ${pct >= 90 ? "bg-red-500" : "bg-[var(--gold)]"}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

export const SubscriberCard = ({ s, profit }) => {
  const { t, fmtDate, fmtNum } = useI18n();
  const money = useMoney();
  const tid = `subs-row-${s.id}`;
  return (
    <li className="card-carbon flex min-w-0 flex-col gap-3 rounded-2xl p-4" data-testid={tid}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-1.5 break-words font-semibold text-white" data-testid={`${tid}-name`}><ProfitDot c={profit} testId={`${tid}-profit`} />{s.name}</p>
          {s.archived && <span className="mt-1 inline-block rounded-full bg-neutral-700 px-2 py-0.5 text-[0.65rem] text-neutral-200" data-testid={`${tid}-archived`}>{t("subs.archived")}</span>}
          {s.extra_employees > 0 && <p className="text-xs text-sky-200" data-testid={`${tid}-employees`}>{t("emp.rowCount", { n: s.extra_employees })}</p>}
          <p className="flex min-w-0 items-center gap-1.5 break-all text-xs text-neutral-400"><Mail className="h-3.5 w-3.5 shrink-0" />{s.email}</p>
        </div>
        <StatusPill status={s.status} testId={`${tid}-status`} />
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--gold-bright)]" data-testid={`${tid}-plan`}>
          {s.status === "vip" ? <Crown className="h-4 w-4 shrink-0" /> : <Package className="h-4 w-4 shrink-0" />} {s.kind === "minisite" ? t("msg.type.minisite_plan") : planLabel(t, s.plan, s.plan_name)}
        </span>
        {s.prepaid_std && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase text-emerald-200" data-testid={`${tid}-prepaid-std`}>{t("subs.prepaidStd", { n: s.prepaid_std.months, date: fmtDate(s.prepaid_std.until) })}</span>}
        {s.prepaid && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase text-emerald-200" data-testid={`${tid}-prepaid`}>{t("subs.prepaidUntil", { date: fmtDate(s.prepaid_until) })}</span>}
        <span className="min-w-0 break-words text-neutral-200" data-testid={`${tid}-price`}>
          {s.price_ht == null ? (s.status === "vip" ? t("subs.free") : "—") : `${money(s.price_ht, s.currency)} ${t("subs.ht")} · ${money(s.price_ttc, s.currency)} ${t("subs.ttc")}`}
        </span>
      </div>
      {s.group && <GroupBlock g={s.group} testId={`${tid}-group`} />}
      {s.business_plans?.length > 0 && <BusinessPlans plans={s.business_plans} testId={`${tid}-business`} />}
      {s.kind !== "minisite" && <div className="space-y-1">
        <p className="text-xs text-neutral-400" data-testid={`${tid}-apps`}>{t("subs.appsMini", { used: fmtNum(s.apps_used), max: fmtNum(s.max_apps), m: fmtNum(s.minisites_used || 0) })}</p>
        <AppsBar used={s.apps_used} max={s.max_apps} />
      </div>}
      {(s.next_renewal || s.ends_at || s.days_left != null) && (
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-neutral-300" data-testid={`${tid}-date`}>
          <CalendarClock className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 break-words">
            {s.next_renewal ? t("subs.renewal", { date: fmtDate(s.next_renewal) }) : s.ends_at ? t("subs.endsOn", { date: fmtDate(s.ends_at) }) : t("subs.graceLeft", { n: s.days_left })}
          </span>
        </p>
      )}
    </li>
  );
};
